/**
 * useClienteExcel - Hook for Excel export/import operations
 *
 * Handles: Export produtos, representantes, hospitais to Excel
 *          Import cliente-hospital relationships via Excel
 */
import { useCallback, useState } from 'react';
import * as XLSX from 'xlsx';
import type { Cliente } from '../services/clienteService';

import type { Representante } from '../services/representantesService';
import type { ClienteHospital } from '../types/clienteHospital';
import { msg, MESSAGES } from '../utils/messages';
import apiClient from '../services/apiClient';

// Import result interface for cliente-hospital
export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
  total_linhas_processadas: number;
}

// Import result interface for representantes
export interface RepresentanteImportResult {
  success: boolean;
  representantes_created: number;
  representantes_updated: number;
  vinculos_created: number;
  errors: string[];
  total_linhas_processadas: number;
}

// Import result interface for clientes bulk upload
export interface ClientesImportResult {
  success: boolean;
  total_linhas: number;
  criados: number;
  atualizados: number;
  erros: string[];
}

export interface UseClienteExcelConfig {
  selectedCliente: Cliente | null;
}

export interface UseClienteExcelReturn {
  exportProdutosToExcel: () => Promise<void>;
  exportingProdutos: boolean;
  exportRepresentantesToExcel: (representantes: Representante[]) => void;
  exportHospitaisToExcel: (hospitais: ClienteHospital[]) => void;
  // Import functions for cliente-hospital
  downloadHospitaisTemplate: () => Promise<void>;
  importHospitaisFromExcel: (file: File) => Promise<ImportResult>;
  importingHospitais: boolean;
  // Import functions for representantes
  downloadRepresentantesTemplate: () => Promise<void>;
  importRepresentantesFromExcel: (file: File) => Promise<RepresentanteImportResult>;
  importingRepresentantes: boolean;
  // Import functions for clientes bulk upload
  importClientesFromExcel: (file: File) => Promise<ClientesImportResult>;
  importingClientes: boolean;
}

export const useClienteExcel = ({
  selectedCliente,
}: UseClienteExcelConfig): UseClienteExcelReturn => {
  const [exportingProdutos, setExportingProdutos] = useState(false);
  const [importingHospitais, setImportingHospitais] = useState(false);
  const [importingRepresentantes, setImportingRepresentantes] = useState(false);
  const [importingClientes, setImportingClientes] = useState(false);

  // Helper to get cliente name for filename
  const getClienteName = useCallback(() => {
    return selectedCliente?.razao_social || selectedCliente?.nome_fantasia || 'cliente';
  }, [selectedCliente]);

  // Export produtos to Excel — backend generates file with ALL products + GTINs
  const exportProdutosToExcel = useCallback(async () => {
    if (!selectedCliente) {
      msg.warning('Selecione um cliente primeiro');
      return;
    }

    setExportingProdutos(true);
    try {
      const response = await apiClient.get(
        `/clientes/${selectedCliente.id}/produtos-cliente/export-excel`,
        { responseType: 'blob' }
      );

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `produtos_${getClienteName().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      msg.success('Exportação concluída');
    } catch (error) {
      console.error('Error exporting produtos:', error);
      msg.error('Erro ao exportar produtos');
    } finally {
      setExportingProdutos(false);
    }
  }, [selectedCliente, getClienteName]);

  // Export representantes to Excel
  const exportRepresentantesToExcel = useCallback((representantes: Representante[]) => {
    if (representantes.length === 0) {
      msg.warning(MESSAGES.CLIENT.EXPORT_EMPTY_REPS);
      return;
    }

    const data = representantes.map(r => ({
      'Nome': r.nome || '',
      'Email': r.email || '',
      'Telefone': r.telefone || '',
      'CPF/CNPJ': r.cpf_cnpj || '',
      'Hospitais Alocados': r.hospitais_count || 0,
      'Ativo': r.ativo ? 'Sim' : 'Não'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Representantes');

    // Adjust column widths
    ws['!cols'] = [
      { wch: 30 }, { wch: 30 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 8 }
    ];

    const fileName = `representantes_${getClienteName().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [getClienteName]);

  // Export hospitais to Excel
  const exportHospitaisToExcel = useCallback((hospitais: ClienteHospital[]) => {
    if (hospitais.length === 0) {
      msg.warning(MESSAGES.CLIENT.EXPORT_EMPTY_HOSPITALS);
      return;
    }

    const data = hospitais.map(h => ({
      // codigo = vinculo ClienteHospital (mesma coluna "Cod. Hospital Cliente"
      // da grid); hospital_codigo seria o codigo do CADASTRO do hospital.
      'Cod. Hospital Cliente': h.codigo || '',
      'Hospital': h.hospital_nome || '',
      'Cidade': h.hospital_cidade || '',
      'Estado': h.hospital_estado || '',
      'CNPJ': h.hospital_cnpj || '',
      'Locais': h.locais_count || 0,
      'Representantes': h.representantes?.length || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hospitais');

    // Adjust column widths
    ws['!cols'] = [
      { wch: 22 }, { wch: 35 }, { wch: 20 }, { wch: 8 }, { wch: 18 }, { wch: 10 }, { wch: 15 }
    ];

    const fileName = `hospitais_${getClienteName().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [getClienteName]);

  // Download template for cliente-hospital import
  const downloadHospitaisTemplate = useCallback(async () => {
    if (!selectedCliente) {
      msg.warning('Selecione um cliente primeiro');
      return;
    }

    try {
      const response = await apiClient.get(
        `/clientes/${selectedCliente.id}/hospitais/template-excel`,
        { responseType: 'blob' }
      );

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_clientes_hospitais_${selectedCliente.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      msg.error('Erro ao baixar template');
    }
  }, [selectedCliente]);

  // Import cliente-hospital relationships from Excel
  const importHospitaisFromExcel = useCallback(async (file: File): Promise<ImportResult> => {
    if (!selectedCliente) {
      return {
        success: false,
        created: 0,
        updated: 0,
        errors: ['Selecione um cliente primeiro'],
        total_linhas_processadas: 0,
      };
    }

    setImportingHospitais(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ImportResult>(
        `/clientes/${selectedCliente.id}/hospitais/bulk-import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = response.data;

      if (result.success) {
        msg.success(`${result.created} vínculo(s) criado(s), ${result.updated} atualizado(s)`);
      } else if (result.errors?.length > 0) {
        msg.warning(`Importação com ${result.errors.length} erro(s)`);
      }

      return result;
    } catch (error: any) {
      console.error('Error importing hospitais:', error);
      msg.error('Erro ao importar arquivo');
      return {
        success: false,
        created: 0,
        updated: 0,
        errors: ['Erro de conexão com o servidor'],
        total_linhas_processadas: 0,
      };
    } finally {
      setImportingHospitais(false);
    }
  }, [selectedCliente]);

  // Download template for representantes import
  const downloadRepresentantesTemplate = useCallback(async () => {
    if (!selectedCliente) {
      msg.warning('Selecione um cliente primeiro');
      return;
    }

    try {
      const response = await apiClient.get(
        `/clientes/${selectedCliente.id}/representantes/template-excel`,
        { responseType: 'blob' }
      );

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_representantes_${selectedCliente.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      msg.error('Erro ao baixar template');
    }
  }, [selectedCliente]);

  // Import representantes from Excel
  const importRepresentantesFromExcel = useCallback(async (file: File): Promise<RepresentanteImportResult> => {
    if (!selectedCliente) {
      return {
        success: false,
        representantes_created: 0,
        representantes_updated: 0,
        vinculos_created: 0,
        errors: ['Selecione um cliente primeiro'],
        total_linhas_processadas: 0,
      };
    }

    setImportingRepresentantes(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<RepresentanteImportResult>(
        `/clientes/${selectedCliente.id}/representantes/bulk-import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = response.data;

      if (result.success) {
        const messages: string[] = [];
        if (result.representantes_created > 0) {
          messages.push(`${result.representantes_created} representante(s) criado(s)`);
        }
        if (result.representantes_updated > 0) {
          messages.push(`${result.representantes_updated} atualizado(s)`);
        }
        if (result.vinculos_created > 0) {
          messages.push(`${result.vinculos_created} vínculo(s) hospital`);
        }
        msg.success(messages.join(', '));
      } else if (result.errors?.length > 0) {
        msg.warning(`Importação com ${result.errors.length} erro(s)`);
      }

      return result;
    } catch (error: any) {
      console.error('Error importing representantes:', error);
      msg.error('Erro ao importar arquivo');
      return {
        success: false,
        representantes_created: 0,
        representantes_updated: 0,
        vinculos_created: 0,
        errors: ['Erro de conexão com o servidor'],
        total_linhas_processadas: 0,
      };
    } finally {
      setImportingRepresentantes(false);
    }
  }, [selectedCliente]);

  // Import clientes from Excel (bulk upload)
  const importClientesFromExcel = useCallback(async (file: File): Promise<ClientesImportResult> => {
    setImportingClientes(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ClientesImportResult>(
        `/clientes/bulk-import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = response.data;

      if (result.success) {
        const messages: string[] = [];
        if (result.criados > 0) {
          messages.push(`${result.criados} cliente(s) criado(s)`);
        }
        if (result.atualizados > 0) {
          messages.push(`${result.atualizados} atualizado(s)`);
        }
        msg.success(messages.join(', ') || 'Importação concluída');
      } else if (result.erros?.length > 0) {
        msg.warning(`Importação com ${result.erros.length} erro(s)`);
      }

      return result;
    } catch (error: any) {
      console.error('Error importing clientes:', error);
      msg.error('Erro ao importar arquivo de clientes');
      return {
        success: false,
        total_linhas: 0,
        criados: 0,
        atualizados: 0,
        erros: ['Erro de conexão com o servidor'],
      };
    } finally {
      setImportingClientes(false);
    }
  }, []);

  return {
    exportProdutosToExcel,
    exportingProdutos,
    exportRepresentantesToExcel,
    exportHospitaisToExcel,
    downloadHospitaisTemplate,
    importHospitaisFromExcel,
    importingHospitais,
    downloadRepresentantesTemplate,
    importRepresentantesFromExcel,
    importingRepresentantes,
    importClientesFromExcel,
    importingClientes,
  };
};
